import React, { useState, useEffect, useRef, useCallback } from 'react';
import useBreakpoint from './hooks/useBreakpoint';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, isFirebaseConfigured } from './firebase';
import { useTheme } from './ThemeContext';
import { loadUserData, saveUserData, deleteUserData, subscribeToUserData } from './services/sync';
import OnboardingScreen from './components/OnboardingScreen';
import NavBar from './components/NavBar';
import AuthScreen from './components/AuthScreen';
import GuestBanner from './components/GuestBanner';
import AccountMenu from './components/AccountMenu';
import UpgradeModal from './components/UpgradeModal';
import BudgetPlanner from './components/BudgetPlanner';
import CalendarView from './components/CalendarView';
import DebtTracker from './components/DebtTracker';
import Dashboard from './components/Dashboard';
import SavingsGoals from './components/SavingsGoals';
import ForecastView from './components/ForecastView';
import SettingsView from './components/SettingsView';
import SpendingLog from './components/SpendingLog';
import './App.css';

const newId = () => Math.random().toString(36).substr(2, 9);

const DEFAULT_TRACKER = {
  id: 'default',
  name: 'My Budget',
  budgetTitle: '',
  budgetItems: [],
  endOfDayBalance: 0,
  oneOffTransactions: {},
  skippedOccurrences: {},
  debtTitle: '',
  debts: [],
  goals: [],
  spendingLog: [],
};

const DEFAULT_ALERT_PREFS = {
  billReminder3d: true,
  billReminder1d: true,
  lowBalance: true,
  lowBalanceThreshold: 100,
  monthlySummary: true,
  overBudget: true,
};

const INITIAL = {
  hasOnboarded: false,
  userName: '',
  currency: 'USD',
  alertPrefs: DEFAULT_ALERT_PREFS,
  view: 'budget',
  activeTrackerId: 'default',
  trackers: [{ ...DEFAULT_TRACKER }],
};

// Migrate old flat state structure → trackers array
function migrateState(parsed) {
  if (parsed && parsed.trackers) {
    // Ensure each tracker has spendingLog
    return {
      ...parsed,
      currency: parsed.currency || 'USD',
      alertPrefs: parsed.alertPrefs || DEFAULT_ALERT_PREFS,
      trackers: parsed.trackers.map(t => ({ spendingLog: [], ...t })),
    };
  }
  const {
    budgetTitle, budgetItems, endOfDayBalance,
    oneOffTransactions, debtTitle, debts,
    isPro: _ip, ...meta
  } = parsed || {};
  return {
    ...INITIAL,
    ...meta,
    currency: meta.currency || 'USD',
    alertPrefs: meta.alertPrefs || DEFAULT_ALERT_PREFS,
    activeTrackerId: 'default',
    trackers: [{
      ...DEFAULT_TRACKER,
      budgetTitle:        budgetTitle        || '',
      budgetItems:        budgetItems        || [],
      endOfDayBalance:    endOfDayBalance    || 0,
      oneOffTransactions: oneOffTransactions || {},
      debtTitle:          debtTitle          || '',
      debts:              debts              || [],
    }],
  };
}

function App() {
  const { t, bigText } = useTheme();
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const isDesktop = bp === 'desktop';

  // ── Auth & UI state ───────────────────────────────────────────────────────
  const [authUser,       setAuthUser]       = useState(null);
  const [authLoading,    setAuthLoading]    = useState(isFirebaseConfigured);
  const [isPro,          setIsPro]          = useState(false);
  const [showUpgrade,    setShowUpgrade]    = useState(false);
  const [syncStatus,     setSyncStatus]     = useState('idle');
  const [showAuth,       setShowAuth]       = useState(false);
  const [authInitialMode,setAuthInitialMode]= useState('signin');
  const [editingId,      setEditingId]      = useState(null);
  const [editingName,    setEditingName]    = useState('');

  // ── App data state ────────────────────────────────────────────────────────
  const [state, setState] = useState(() => INITIAL);

  const isCloudLoadedRef = useRef(false);
  const syncTimerRef     = useRef(null);

  // ── Firebase auth listener ────────────────────────────────────────────────
  useEffect(() => {
    if (!isFirebaseConfigured) return;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);

      if (user) {
        setSyncStatus('syncing');
        try {
          const data = await loadUserData(user.uid);
          if (data?.appState) {
            const migrated = migrateState(data.appState);
            setState(() => ({ ...INITIAL, ...migrated, view: migrated.view || 'budget' }));
          }
          setIsPro(data?.isPro ?? false);
          isCloudLoadedRef.current = true;
          setSyncStatus('synced');
        } catch {
          setSyncStatus('error');
          isCloudLoadedRef.current = true;
        }

        const unsubData = subscribeToUserData(user.uid, (data) => {
          if (data?.isPro !== undefined) setIsPro(data.isPro);
        });
        return () => unsubData();
      } else {
        isCloudLoadedRef.current = false;
        setIsPro(false);
        setState(INITIAL);
      }

      setAuthLoading(false);
    });

    return unsubscribeAuth;
  }, []);

  // ── Debounced sync ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isFirebaseConfigured || !authUser || !isCloudLoadedRef.current) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    setSyncStatus('syncing');
    syncTimerRef.current = setTimeout(async () => {
      try { await saveUserData(authUser.uid, state); setSyncStatus('synced'); }
      catch { setSyncStatus('error'); }
    }, 1500);

    return () => { if (syncTimerRef.current) clearTimeout(syncTimerRef.current); };
  }, [state, authUser]);

  // ── State helpers ─────────────────────────────────────────────────────────
  const set = useCallback((updates) => setState((prev) => ({ ...prev, ...updates })), []);

  const activeTracker = state.trackers.find((tr) => tr.id === state.activeTrackerId)
    || state.trackers[0]
    || DEFAULT_TRACKER;

  const setTracker = useCallback((updates) => {
    setState((prev) => ({
      ...prev,
      trackers: prev.trackers.map((tr) =>
        tr.id === prev.activeTrackerId ? { ...tr, ...updates } : tr
      ),
    }));
  }, []);

  const addTracker = () => {
    if (!isPro) { setShowUpgrade(true); return; }
    const id = newId();
    setState((prev) => ({
      ...prev,
      trackers: [...prev.trackers, {
        ...DEFAULT_TRACKER,
        id,
        name: `Tracker ${prev.trackers.length + 1}`,
      }],
      activeTrackerId: id,
    }));
  };

  const deleteTracker = (id) => {
    setState((prev) => ({
      ...prev,
      trackers: prev.trackers.filter((tr) => tr.id !== id),
      activeTrackerId:
        prev.activeTrackerId === id
          ? prev.trackers.find((tr) => tr.id !== id)?.id || 'default'
          : prev.activeTrackerId,
    }));
  };

  const saveTrackerName = () => {
    if (!editingName.trim()) { setEditingId(null); return; }
    setState((prev) => ({
      ...prev,
      trackers: prev.trackers.map((tr) =>
        tr.id === editingId ? { ...tr, name: editingName.trim() } : tr
      ),
    }));
    setEditingId(null);
  };

  // ── Data export ───────────────────────────────────────────────────────────
  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'budget-planner-data.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const log = activeTracker.spendingLog || [];
    const rows = [
      ['Type', 'Name', 'Category', 'Amount', 'Frequency'],
      ...activeTracker.budgetItems.map((i) => [i.type, i.name, i.category || '', i.amount, i.frequency]),
      [],
      ['Debt', 'Balance', 'Interest Rate', 'Min Payment'],
      ...activeTracker.debts.map((d) => [d.name, d.balance, d.interestRate + '%', d.minPayment]),
      [],
      ['Date', 'Name', 'Category', 'Amount', 'Type'],
      ...log.map((e) => [e.date, e.name, e.category || '', e.amount, e.type]),
    ];
    const csv  = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'budget-planner-data.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteAccount = async () => {
    if (!authUser) return;
    await deleteUserData(authUser.uid);
    await authUser.delete();
    setState(INITIAL);
    localStorage.removeItem('bpp_state');
  };

  const handleSignOut = async () => { await signOut(auth); setState(INITIAL); };

  const handleManageSubscription = async () => {
    if (!authUser) return;
    try {
      const res = await fetch('/api/create-portal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: authUser.uid }),
      });
      if (!res.ok) throw new Error('Could not open portal');
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      alert('Unable to open subscription portal. Please try again.');
    }
  };

  const handleReset = async () => {
    const confirmed = window.confirm(
      'This will delete all your budget data and restart the setup wizard. Are you sure?'
    );
    if (!confirmed) return;
    localStorage.removeItem('bpp_state');
    if (authUser) {
      try { await saveUserData(authUser.uid, { ...INITIAL, hasOnboarded: false }); } catch {}
    }
    setState({ ...INITIAL, hasOnboarded: false });
  };

  // ── Derived data ──────────────────────────────────────────────────────────
  const debtPaymentItems = activeTracker.debts.flatMap((d) =>
    (d.payments || []).map((p) => ({
      ...p,
      type: 'expense',
      name: p.name || `${d.name} Payment`,
      isDebtPayment: true,
      debtId: d.id,
    }))
  );

  // ── Render guards ─────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: t.pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: '800', letterSpacing: '-0.02em', marginBottom: '12px', color: t.text }}>BPP</div>
          <div style={{ fontSize: '14px', color: t.textMuted }}>Loading…</div>
        </div>
      </div>
    );
  }

  const hasLocalData = (() => { try { return !!localStorage.getItem('bpp_state'); } catch { return false; } })();

  if (isFirebaseConfigured && !authUser && !hasLocalData) {
    return <AuthScreen onAuthSuccess={(user) => setAuthUser(user)} />;
  }

  const isGuest = isFirebaseConfigured && !authUser && hasLocalData;

  if (showAuth) {
    return (
      <AuthScreen
        initialMode={authInitialMode}
        onAuthSuccess={(user) => { setAuthUser(user); setShowAuth(false); }}
        onCancel={isGuest ? () => setShowAuth(false) : undefined}
      />
    );
  }

  if (!state.hasOnboarded) {
    return (
      <OnboardingScreen
        onComplete={({ userName, balance, items, currency }) =>
          set({
            hasOnboarded: true, userName, currency: currency || 'USD', view: 'budget',
            trackers: [{ ...DEFAULT_TRACKER, budgetItems: items, endOfDayBalance: balance }],
          })
        }
        onSkip={() => set({ hasOnboarded: true, view: 'budget' })}
      />
    );
  }

  // Tracker bar sizing
  const trackerBarHeight = isDesktop ? '38px' : isTablet ? '36px' : '34px';
  const trackerBarPadding = isDesktop ? '0 20px' : isTablet ? '0 16px' : '0 12px';
  const trackerTabFontSize = isTablet ? '12px' : isMobile ? '11px' : '13px';

  // Auth button sizing
  const authBtnPadding = isMobile ? '5px 8px' : isTablet ? '5px 11px' : '6px 13px';
  const authBtnFontSize = isMobile ? '11px' : isTablet ? '12px' : '13px';

  const rightSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      {!isMobile && (
        <button
          onClick={() => set({ view: 'settings' })}
          style={{
            background: 'transparent',
            color: state.view === 'settings' ? '#0061a4' : '#707883',
            border: 'none',
            borderBottom: state.view === 'settings' ? '2px solid #0061a4' : '2px solid transparent',
            borderRadius: '0',
            padding: isTablet ? '7px 10px' : '8px 12px',
            cursor: 'pointer', fontSize: isTablet ? '13px' : '14px',
            fontWeight: state.view === 'settings' ? '600' : '400',
            fontFamily: 'inherit', whiteSpace: 'nowrap',
            marginRight: '28px',
          }}
        >
          Settings
        </button>
      )}
      {isPro ? (
        <span style={{
          fontSize: '11px', fontWeight: '700', padding: '4px 10px',
          background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
          color: 'white', borderRadius: '20px', letterSpacing: '0.04em',
        }}>PRO</span>
      ) : (
        <button onClick={() => setShowUpgrade(true)} style={{
          fontSize: authBtnFontSize, fontWeight: '600',
          padding: authBtnPadding,
          background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
          color: 'white', border: 'none', borderRadius: '20px',
          cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}>
          {isMobile ? 'Pro' : 'Upgrade to Pro'}
        </button>
      )}
      {isFirebaseConfigured && authUser ? (
        <AccountMenu
          authUser={authUser}
          isPro={isPro}
          syncStatus={syncStatus}
          onSignOut={handleSignOut}
          onDeleteAccount={handleDeleteAccount}
          onReset={handleReset}
          onManageSubscription={handleManageSubscription}
        />
      ) : isFirebaseConfigured ? (
        <>
          <button
            onClick={() => { setAuthInitialMode('signin'); setShowAuth(true); }}
            style={{
              background: 'transparent', border: 'none',
              borderRadius: '7px', padding: authBtnPadding,
              fontSize: authBtnFontSize, color: t.textSecondary,
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? 'In' : 'Sign In'}
          </button>
          <button
            onClick={() => { setAuthInitialMode('register'); setShowAuth(true); }}
            style={{
              background: '#0061a4', border: 'none',
              borderRadius: '7px', padding: authBtnPadding,
              fontSize: authBtnFontSize, fontWeight: '600', color: 'white',
              cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? 'Up' : 'Sign Up'}
          </button>
        </>
      ) : null}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ zoom: bigText ? 1.15 : 1 }}>
      <NavBar
        currentView={state.view}
        onNavigate={(v) => set({ view: v })}
        rightSlot={rightSlot}
        isPro={isPro}
      />

      {/* ── Tracker bar (Pro only) ── */}
      {!isPro && authUser && (
        <div style={{ background: t.card, borderBottom: `1px solid ${t.border}` }}>
          <div style={{ maxWidth: '1140px', margin: '0 auto', padding: trackerBarPadding, height: trackerBarHeight, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: t.textMuted }}>
              Manage multiple budgets with Pro
            </span>
            <button
              onClick={() => setShowUpgrade(true)}
              style={{ fontSize: '11px', fontWeight: '600', padding: '3px 10px', background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Upgrade
            </button>
          </div>
        </div>
      )}
      {isPro && (
        <div style={{ background: t.card, borderBottom: `1px solid ${t.border}` }}>
          <div style={{
            maxWidth: '1140px', margin: '0 auto', padding: trackerBarPadding,
            display: 'flex', alignItems: 'center', height: trackerBarHeight,
            gap: '2px', overflowX: 'auto',
          }}>
            {state.trackers.map((tracker) => (
              <div key={tracker.id} style={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0 }}>
                {editingId === tracker.id ? (
                  <input
                    autoFocus
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={saveTrackerName}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTrackerName();
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    style={{
                      fontSize: trackerTabFontSize, fontWeight: '600', color: '#0061a4',
                      border: 'none', borderBottom: '2px solid #0061a4', outline: 'none',
                      background: 'transparent', fontFamily: 'inherit', padding: '2px 4px',
                      width: `${Math.max(editingName.length, 4) + 2}ch`,
                    }}
                  />
                ) : (
                  <button
                    onClick={() => setState((prev) => ({ ...prev, activeTrackerId: tracker.id }))}
                    onDoubleClick={() => {
                      setEditingId(tracker.id);
                      setEditingName(tracker.name);
                    }}
                    title="Double-click to rename"
                    style={{
                      background: tracker.id === state.activeTrackerId ? t.card : 'transparent',
                      color:      tracker.id === state.activeTrackerId ? '#0061a4' : '#707883',
                      border: 'none', borderRadius: '6px', padding: '4px 10px',
                      boxShadow: tracker.id === state.activeTrackerId ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      fontSize: trackerTabFontSize, cursor: 'pointer', fontFamily: 'inherit',
                      fontWeight: tracker.id === state.activeTrackerId ? '600' : '400',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tracker.name}
                  </button>
                )}
                {state.trackers.length > 1 && tracker.id === state.activeTrackerId && (
                  <button
                    onClick={() => window.confirm(`Delete "${tracker.name}"?`) && deleteTracker(tracker.id)}
                    title="Delete tracker"
                    style={{ background: 'none', border: 'none', color: t.borderFaint, cursor: 'pointer', fontSize: '15px', padding: '0 3px', lineHeight: 1, fontFamily: 'inherit' }}
                  >×</button>
                )}
              </div>
            ))}

            <button
              onClick={addTracker}
              style={{
                background: 'transparent', border: '1px dashed rgba(191,199,212,0.6)', borderRadius: '6px',
                padding: '3px 10px', fontSize: '12px', color: '#0061a4',
                cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', marginLeft: '6px',
              }}
            >
              + New Tracker
            </button>
          </div>
        </div>
      )}

      {isGuest && (
        <GuestBanner
          onSignIn={() => { setAuthInitialMode('signin'); setShowAuth(true); }}
          onCreateAccount={() => { setAuthInitialMode('register'); setShowAuth(true); }}
        />
      )}

      {state.view === 'budget' && (
        <BudgetPlanner
          title={activeTracker.budgetTitle}
          items={activeTracker.budgetItems}
          debtPaymentItems={debtPaymentItems}
          onTitleChange={(title) => setTracker({ budgetTitle: title })}
          onItemsChange={(items) => setTracker({ budgetItems: items })}
          currency={state.currency}
        />
      )}
      {state.view === 'calendar' && (
        <CalendarView
          budgetItems={[...activeTracker.budgetItems, ...debtPaymentItems]}
          endOfDayBalance={activeTracker.endOfDayBalance}
          oneOffTransactions={activeTracker.oneOffTransactions}
          skippedOccurrences={activeTracker.skippedOccurrences || {}}
          onUpdateBalance={(b) => setTracker({ endOfDayBalance: b })}
          onUpdateTransactions={(tr) => setTracker({ oneOffTransactions: tr })}
          onBudgetItemsChange={(items) => setTracker({ budgetItems: items })}
          onUpdateSkips={(s) => setTracker({ skippedOccurrences: s })}
          isPro={isPro}
          currency={state.currency}
          onAddToSpendingLog={isPro ? (entry) => setTracker({ spendingLog: [...(activeTracker.spendingLog || []), entry] }) : null}
        />
      )}
      {state.view === 'forecast' && (
        <ForecastView
          budgetItems={[...activeTracker.budgetItems, ...debtPaymentItems]}
          endOfDayBalance={activeTracker.endOfDayBalance}
          isPro={isPro}
          currency={state.currency}
        />
      )}
      {state.view === 'debt' && (
        <DebtTracker
          title={activeTracker.debtTitle}
          debts={activeTracker.debts}
          isAuthenticated={!!authUser}
          isPro={isPro}
          onTitleChange={(title) => setTracker({ debtTitle: title })}
          onDebtsChange={(d) => setTracker({ debts: d })}
          onSignInClick={() => { setAuthInitialMode('register'); setShowAuth(true); }}
          onUpgradeClick={() => setShowUpgrade(true)}
          currency={state.currency}
        />
      )}
      {state.view === 'goals' && (
        <SavingsGoals
          goals={activeTracker.goals || []}
          isAuthenticated={!!authUser}
          onGoalsChange={(goals) => setTracker({ goals })}
          onSignInClick={() => { setAuthInitialMode('register'); setShowAuth(true); }}
          currency={state.currency}
        />
      )}
      {state.view === 'dashboard' && (
        <Dashboard
          budgetItems={activeTracker.budgetItems}
          debtPaymentItems={debtPaymentItems}
          endOfDayBalance={activeTracker.endOfDayBalance}
          debts={activeTracker.debts}
          spendingLog={activeTracker.spendingLog || []}
          isAuthenticated={!!authUser}
          isPro={isPro}
          onUpgradeClick={() => setShowUpgrade(true)}
          currency={state.currency}
        />
      )}
      {state.view === 'spending' && (
        <SpendingLog
          spendingLog={activeTracker.spendingLog || []}
          budgetItems={activeTracker.budgetItems}
          onSpendingLogChange={(log) => setTracker({ spendingLog: log })}
          isPro={isPro}
          onUpgradeClick={() => setShowUpgrade(true)}
          currency={state.currency}
        />
      )}
      {state.view === 'settings' && (
        <SettingsView
          isAuthenticated={!!authUser}
          isPro={isPro}
          onExportJSON={handleExportJSON}
          onExportCSV={handleExportCSV}
          onReset={handleReset}
          onSignInClick={() => { setAuthInitialMode('register'); setShowAuth(true); }}
          onUpgradeClick={() => setShowUpgrade(true)}
          currency={state.currency}
          onCurrencyChange={(c) => set({ currency: c })}
          alertPrefs={state.alertPrefs || DEFAULT_ALERT_PREFS}
          onAlertPrefsChange={(p) => set({ alertPrefs: p })}
        />
      )}

      {showUpgrade && (
        <UpgradeModal
          authUser={authUser}
          onClose={() => setShowUpgrade(false)}
          onProActivated={() => { setIsPro(true); setShowUpgrade(false); }}
        />
      )}
    </div>
  );
}

export default App;
